import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { ref } from '@vue/composition-api'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import { createHistoryEntry } from '@/helpers/changeDetection'

export default function useInherentRiskAdd() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { router } = useRouter()
  const { createItem, updateItem } = useLambda()

  const userData = store.state?.userStore?.userData
  const client_id = userData?.client?.$oid
  const userId = userData?.worker_id?.$oid
  const isSubmitting = ref(false)
  const collection = 'inherentAnalysis'
  const ariData = ref({
    scores: {},
    legalObligations: {
      ol_a: false,
      ol_b: false,
      ol_c: false,
      ol_d: false,
    },
  })
  const ariId = ref(null) // Store ID after first draft save

  // Build payload helper
  const buildPayload = (data, status = null) => {
    const payload = {
      client_id: oid(client_id),
      treatment_id: data.treatment_id ? oid(data.treatment_id) : null,
      scores: data.scores || {},
      legalObligations: data.legalObligations || {
        ol_a: false,
        ol_b: false,
        ol_c: false,
        ol_d: false,
      },
      calculatedP: data.calculatedP || null,
      calculatedI: data.calculatedI || null,
      inherentRisk: data.inherentRisk || null,
      inherentLevel: data.inherentLevel || null,
      requiresEipdAuto: data.requiresEipdAuto || false,
      eipdOverride: data.eipdOverride ?? null,
      eipdOverrideJustification: data.eipdOverrideJustification || '',
      eipdFinal: data.eipdFinal || false,
    }

    // Only update status if explicitly provided
    if (status !== null) {
      payload.status = status
    }

    return payload
  }

  // Save as draft (for autosave)
  const saveDraft = async (data) => {
    try {
      if (!ariId.value) {
        // First save: create document
        const historyEntry = createHistoryEntry(userId, 'create_draft', [])

        const payload = {
          ...buildPayload(data, 'draft'),
          history: [historyEntry],
        }

        const result = await createItem({ collection, payload })

        if (result.insertedId) {
          ariId.value = result.insertedId
        }
      } else {
        // Update existing draft
        const query = { _id: oid(ariId.value) }
        const payload = buildPayload(data, 'draft')
        const action = { $set: payload }

        await updateItem({ collection, query, action })
      }
    } catch (error) {
      console.error('Error saving draft:', error)
      throw error
    }
  }

  // Final create (marks as active)
  const create = async (data) => {
    if (!hasPermission('privacy-inherent-risk', 'create')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      isSubmitting.value = true

      if (ariId.value) {
        // Already has draft, just activate it
        const historyEntry = createHistoryEntry(userId, 'activate', [])

        const query = { _id: oid(ariId.value) }
        const payload = buildPayload(data, 'active')
        const action = {
          $set: payload,
          $push: { history: historyEntry },
        }

        await updateItem({ collection, query, action })
      } else {
        // Direct creation without draft
        const historyEntry = createHistoryEntry(userId, 'create', [])

        const payload = {
          ...buildPayload(data, 'active'),
          history: [historyEntry],
        }

        await createItem({ collection, payload })
      }

      showSuccessMessage('ARI creado correctamente')
      router.push({ name: 'privacy-inherent-risk-list' })
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al crear ARI')
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    create,
    saveDraft,
    ariData,
    isSubmitting,
  }
}
